import type { GridCustomCellProps } from "@tql/ui-components-react/kendo/grid";
import { Button } from "@tql/ui-components-react/tql/buttons";
import {
	copy_02Line,
	edit_05,
	trash_01,
} from "@tql/ui-components-react/untitled/icons";
import React, { useCallback, useMemo, useState } from "react";

import { DefaultFlagCell } from "~/shared/components/trax-grid/default-flag-cell/default-flag-cell.tsx";
import { GridTd } from "~/shared/components/trax-grid/grid-td/grid-td.tsx";
import { LoaderCell } from "~/shared/components/trax-grid/loader-cell/loader-cell.tsx";
import { ConfigurationType } from "~/shared/schemas/config-schema.ts";
import type { TemplateGridItem } from "~/shared/schemas/template-schema.ts";
import type { BoardColumn } from "~/shared/types/quote-board-types.ts";

import { CopyTemplateModal } from "../components/copy-template-modal/copy-template-modal.tsx";
import { DeleteTemplateModal } from "../components/delete-template-modal/delete-template-modal.tsx";
import { useDeleteTemplate, useTemplateDefault } from "./use-templates.ts";

type DeleteConfirmationState = {
	isOpen: boolean;
	templateId: number | null;
	templateName: string;
};

type CopyConfirmationState = {
	isOpen: boolean;
	templateId: number | null;
	templateName: string;
};

export type TemplateColumnsProps = {
	readonly customerId: number | undefined;
	readonly isLoading?: boolean;
	readonly setSelectedTemplateItem: (id: number | undefined) => void;
	readonly onCopyTemplate: (id: number) => void | Promise<void>;
};

export type UseTemplateColumnsReturn = {
	templateColumns: BoardColumn[];
	isDeleting: boolean;
	isHandlingDefault: boolean;
	DeleteConfirmationModal: () => React.JSX.Element | null;
	CopyConfirmationModal: () => React.JSX.Element | null;
};

/**
 * The string-valued `TemplateGridItem` fields shown as plain data cells.
 */
type TemplateStringField =
	| "templateName"
	| "shipperName"
	| "pickupLocation"
	| "deliveryName"
	| "deliveryLocation"
	| "commodity"
	| "nmfc"
	| "freightClass"
	| "quantity"
	| "account";

/**
 * Renders a data cell that exposes a stable `data-testid`, so the row's
 * contract does not depend on column order/visibility (positional cells).
 */
const renderDataCell = (field: TemplateStringField, testId: string) =>
	function DataCell(props: GridCustomCellProps) {
		const dataItem = props.dataItem as TemplateGridItem;
		return (
			<GridTd data-testid={testId} tdProps={props.tdProps}>
				{dataItem[field]}
			</GridTd>
		);
	};

export const useTemplateColumns = ({
	customerId,
	isLoading,
	setSelectedTemplateItem,
	onCopyTemplate,
}: TemplateColumnsProps): UseTemplateColumnsReturn => {
	const { deleteTemplate, isDeleting } = useDeleteTemplate();
	const { toggleDefault, isHandlingDefault, userConfigs } =
		useTemplateDefault(customerId);

	const isAnyLoading = !!isLoading || isDeleting || isHandlingDefault;

	const [deleteConfirmation, setDeleteConfirmation] =
		useState<DeleteConfirmationState>({
			isOpen: false,
			templateId: null,
			templateName: "",
		});

	const [copyConfirmation, setCopyConfirmation] =
		useState<CopyConfirmationState>({
			isOpen: false,
			templateId: null,
			templateName: "",
		});

	const openDeleteConfirmation = useCallback(
		(templateId: number, templateName: string) => {
			setDeleteConfirmation({
				isOpen: true,
				templateId,
				templateName,
			});
		},
		[],
	);

	const closeDeleteConfirmation = useCallback(() => {
		setDeleteConfirmation({
			isOpen: false,
			templateId: null,
			templateName: "",
		});
	}, []);

	const openCopyConfirmation = useCallback(
		(templateId: number, templateName: string) => {
			setCopyConfirmation({
				isOpen: true,
				templateId,
				templateName,
			});
		},
		[],
	);

	const closeCopyConfirmation = useCallback(() => {
		setCopyConfirmation({
			isOpen: false,
			templateId: null,
			templateName: "",
		});
	}, []);

	const handleConfirmDelete = useCallback(async () => {
		if (deleteConfirmation.templateId !== null) {
			await deleteTemplate(deleteConfirmation.templateId);
		}
		closeDeleteConfirmation();
	}, [deleteTemplate, deleteConfirmation.templateId, closeDeleteConfirmation]);

	const DeleteConfirmationModalComponent = useCallback(
		() =>
			deleteConfirmation.isOpen ? (
				<DeleteTemplateModal
					onClose={closeDeleteConfirmation}
					onConfirm={() => {
						void handleConfirmDelete();
					}}
					templateName={deleteConfirmation.templateName}
				/>
			) : null,
		[
			deleteConfirmation.isOpen,
			deleteConfirmation.templateName,
			closeDeleteConfirmation,
			handleConfirmDelete,
		],
	);

	const handleConfirmCopy = useCallback(async () => {
		if (copyConfirmation.templateId !== null) {
			await onCopyTemplate(copyConfirmation.templateId);
		}
		closeCopyConfirmation();
	}, [closeCopyConfirmation, copyConfirmation.templateId, onCopyTemplate]);

	const CopyConfirmationModalComponent = useCallback(
		() =>
			copyConfirmation.isOpen ? (
				<CopyTemplateModal
					onClose={closeCopyConfirmation}
					onConfirm={() => {
						void handleConfirmCopy();
					}}
					templateName={copyConfirmation.templateName}
				/>
			) : null,
		[
			closeCopyConfirmation,
			copyConfirmation.isOpen,
			copyConfirmation.templateName,
			handleConfirmCopy,
		],
	);

	// TODO: Temporarily hide copy action until it's ready to be released
	const SHOW_COPY_ACTION = false;

	const templateColumns = useMemo(
		(): BoardColumn[] => [
			{
				field: "templateName",
				title: "Template Name",
				show: true,
				width: 140,
				editable: false,
				cells: {
					data: renderDataCell("templateName", "templates-cell-template-name"),
				},
			},
			{
				field: "shipperName",
				title: "Shipper Name",
				show: true,
				width: 160,
				editable: false,
				cells: {
					data: renderDataCell("shipperName", "templates-cell-shipper-name"),
				},
			},
			{
				field: "pickupLocation",
				title: "Pickup Location",
				show: true,
				width: 155,
				editable: false,
				cells: {
					data: renderDataCell(
						"pickupLocation",
						"templates-cell-pickup-location",
					),
				},
			},
			{
				field: "deliveryName",
				title: "Delivery Name",
				show: true,
				width: 175,
				editable: false,
				cells: {
					data: renderDataCell("deliveryName", "templates-cell-delivery-name"),
				},
			},
			{
				field: "deliveryLocation",
				title: "Delivery Location",
				show: true,
				width: 170,
				editable: false,
				cells: {
					data: renderDataCell(
						"deliveryLocation",
						"templates-cell-delivery-location",
					),
				},
			},
			{
				field: "commodity",
				title: "Commodity",
				show: true,
				width: 145,
				editable: false,
				cells: {
					data: renderDataCell("commodity", "templates-cell-commodity"),
				},
			},
			{
				field: "nmfc",
				title: "NMFC",
				show: true,
				width: 80,
				editable: false,
				cells: {
					data: renderDataCell("nmfc", "templates-cell-nmfc"),
				},
			},
			{
				field: "freightClass",
				title: "Class",
				show: true,
				width: 65,
				editable: false,
				cells: {
					data: renderDataCell("freightClass", "templates-cell-freight-class"),
				},
			},
			{
				field: "quantity",
				title: "Quantity",
				show: true,
				width: 95,
				editable: false,
				cells: {
					data: renderDataCell("quantity", "templates-cell-quantity"),
				},
			},
			{
				field: "account",
				title: "Account",
				show: true,
				width: 100,
				editable: false,
				cells: {
					data: renderDataCell("account", "templates-cell-account"),
				},
			},
			{
				title: "Default",
				width: 60,
				show: true,
				field: "isDefault",
				filterable: false,
				editable: false,
				reorderable: false,
				resizable: false,
				cells: {
					data: (props: GridCustomCellProps) => {
						if (isAnyLoading) {
							return <LoaderCell {...props} />;
						}
						const dataItem = props.dataItem as TemplateGridItem;
						const isDefault = userConfigs.some(
							(c) =>
								c.configType === ConfigurationType.QuoteTemplateDefault &&
								Number(c.customerId) === Number(customerId) &&
								c.configValue === String(dataItem.templateId),
						);
						return (
							<DefaultFlagCell
								{...props}
								data-testid={"templates-cell-default"}
								isDefault={isDefault}
								onToggleDefault={(shouldBeDefault) =>
									toggleDefault(dataItem.templateId, shouldBeDefault)
								}
							/>
						);
					},
				},
			},
			{
				title: "Action",
				width: 80,
				show: true,
				field: "action",
				filterable: false,
				editable: false,
				reorderable: false,
				resizable: false,
				cells: {
					data: (props: GridCustomCellProps) => {
						const dataItem = props.dataItem as TemplateGridItem;
						if (isAnyLoading) {
							return <LoaderCell {...props} />;
						}
						return (
							<GridTd
								data-testid={"templates-cell-actions"}
								tdProps={props.tdProps}
							>
								<div className={"templates-row-actions"}>
									<Button
										data-testid={"templates-action-edit"}
										onClick={() => setSelectedTemplateItem(dataItem.templateId)}
										svgIcon={edit_05}
										variant={"tertiary"}
									/>
									{SHOW_COPY_ACTION && (
										<Button
											data-testid={"templates-action-copy"}
											onClick={() => {
												openCopyConfirmation(
													dataItem.templateId,
													dataItem.templateName,
												);
											}}
											svgIcon={copy_02Line}
											variant={"tertiary"}
										/>
									)}
									<Button
										data-testid={"templates-action-delete"}
										onClick={() => {
											openDeleteConfirmation(
												dataItem.templateId,
												dataItem.templateName,
											);
										}}
										svgIcon={trash_01}
										variant={"dangerTertiary"}
									/>
								</div>
							</GridTd>
						);
					},
				},
			},
		],
		[
			isAnyLoading,
			userConfigs,
			customerId,
			toggleDefault,
			SHOW_COPY_ACTION,
			setSelectedTemplateItem,
			openCopyConfirmation,
			openDeleteConfirmation,
		],
	);

	return {
		templateColumns,
		isDeleting,
		isHandlingDefault,
		DeleteConfirmationModal: DeleteConfirmationModalComponent,
		CopyConfirmationModal: CopyConfirmationModalComponent,
	};
};
